import * as MuiIcons from "@mui/icons-material";
import { Euro, Percent } from "@mui/icons-material";
import * as Mui from '@mui/material';
import { Grid, InputAdornment, MenuItem } from '@mui/material';
import { useContext } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormSelect from "../../../experiments/forms/FormSelect";
import { transactionType } from '../constants/ThresholdsConstants';
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import FormTextField from "../../../experiments/forms/FormTextField";
import { useCashBackRepository } from "../../../api/repositories/CashbackRepository";
import { enrichUpdateThreshold } from "../util/CashbackUtil";
import { useRecoilState } from "recoil";
import { STATE_THRESHOLD_UPDATING } from "../constants/CashbackRecoil";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";

interface Props {
  open: boolean;
  threshold: any;
  onClose: () => void;
}

const EditThreshold = (props: Props) => {

  const { t } = useTranslation('finance', { keyPrefix: 'cashback' });
  const schema = Yup.object().shape({
    threshold: Yup.string().nullable().required(),
    percentage: Yup.number().nullable().required().transform((value, originalValue) => {
      return originalValue === '' ? undefined : parseFloat(originalValue);
    }).max(100, t("filters.errors.invalid-percentage")).nullable().min(0, t("filters.errors.invalid-percentage")).nullable(),
    typology: Yup.string().nullable().required(),

  });

  const { updateThreshold } = useCashBackRepository();
  const { dispatchGlobalData } = useContext(GlobalContext);
  const [updating, setUpdating] = useRecoilState(STATE_THRESHOLD_UPDATING);
  const { handleSubmit, control, register } = useForm<any>({
    defaultValues: {
      threshold: props.threshold.threshold / 100,
      percentage: props.threshold?.percentage,
      typology: props.threshold?.typology
    },
    resolver: yupResolver(schema),
  });



  const onSubmit: SubmitHandler<any> = (data) => {
    enrichUpdateThreshold(data, props);
    updateThreshold(data).then(() => {
      setUpdating(true);
      dispatchGlobalData({
        type: setSnackbar,
        payload: {
          snackbar: {
            message: t('tables.threshold.snackbar.success-update'),
            type: snackbarTypes.success,
            show: true,
            autoClose: true
          }
        }
      });
      onClose();
    }).catch(() => {
      dispatchGlobalData({
        type: setSnackbar,
        payload: {
          snackbar: {
            message: t('tables.threshold.snackbar.error-update'),
            type: snackbarTypes.error,
            show: true,
            autoClose: true
          }
        }
      });
    });
    onClose();
  }

  const onClose = () => {
    props.onClose();
  };

  return (
    <Mui.Dialog open={props.open} onClose={onClose} fullWidth maxWidth="lg">
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Mui.DialogTitle>
          <Mui.Grid container alignItems="center">
            <Mui.Grid item xs="auto">
              <Mui.Typography variant="h6">
                {t("tables.threshold.editTitle", { customerName: props?.threshold?.['customer-name'] })}
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid item xs="auto" marginLeft="auto">
              <Mui.IconButton onClick={onClose}>
                <MuiIcons.Close />
              </Mui.IconButton>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.DialogTitle>
        <Mui.DialogContent>

          <Grid container spacing={2} alignItems={'flex-end'}>

            <Grid item md={3} sm={6} xs={12}>
              <FormTextField {...register('threshold')}
                control={control}
                props={{
                  variant: "standard", fullWidth: true, label: t('threshold-wizard.form.threshold-label'),
                  type: "number",
                  InputProps: {
                    startAdornment:
                      <InputAdornment position="start">
                        <Euro style={{ fontSize: '15px' }} />
                      </InputAdornment>

                  },
                }}
              />
            </Grid>
            <Grid item md={3} sm={6} xs={12}>
              <FormTextField {...register('percentage')}
                control={control}
                props={{
                  variant: "standard", fullWidth: true, label: t('threshold-wizard.form.percentage-label'),
                  type: "number",
                  InputProps: {
                    endAdornment:
                      <InputAdornment position="start">
                        <Percent style={{ fontSize: '15px' }} />
                      </InputAdornment>
                  },
                }}

              />
            </Grid>
            <Grid item md={3} sm={6} xs={12}>
              <FormSelect {...register('typology')}
                control={control}
                props={{
                  variant: "standard", fullWidth: true, label: t('threshold-wizard.form.typology-label'),
                }}
                items={transactionType.map((item: any, index: number) => (
                  <MenuItem key={index} value={item.value}>
                    {t(item.label)}
                  </MenuItem>
                ))} />
            </Grid>
          </Grid>

        </Mui.DialogContent>
        <Mui.DialogActions>
          <Mui.Grid container justifyContent="end" spacing="16px">
            <Mui.Grid item xs="auto" height='48px'>
              <Mui.Button variant="outlined" size="medium" sx={{ maxWidth: '111px', minWidth: "111px", fontWeight: 700 }} onClick={onClose}>
                {t(
                  "tables.threshold.actionButtons.cancel"
                )}
              </Mui.Button>
            </Mui.Grid>
            <Mui.Grid item xs="auto">
              <Mui.Button type="submit" size="medium" sx={{ maxWidth: '111px', minWidth: "111px", fontWeight: 700 }} >
                {t("tables.threshold.actionButtons.save")}
              </Mui.Button>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.DialogActions>

      </form>
    </Mui.Dialog>

  );
};

export default EditThreshold;



